import { motion } from "framer-motion";
import { Compass, Users, Ticket } from "lucide-react";
import { Reveal, SectionHead, Eyebrow } from "@/components/Reveal";
import ApplicationForm from "@/components/ApplicationForm";
import CTABand from "@/components/CTABand";

const ease = [0.16, 1, 0.3, 1];

const OFFICE_IMG =
  "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjB0ZWFtJTIwbWVldGluZyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3OTA3ODg0MTh8MA&ixlib=rb-4.1.0&q=85";

const HIRING = [
  { phase: "Months 1-2", roles: ["Founder", "CTO", "Senior Developer", "UI/UX Designer"] },
  { phase: "Months 3-4", roles: ["Biz Dev Lead", "Marketing Manager"] },
  { phase: "Months 5-6", roles: ["Community Manager", "Customer Success Rep"] },
  { phase: "Months 7-8", roles: ["Sales Representatives (×2)"] },
  { phase: "Months 9-12", roles: ["Operations Director", "Support Staff"] },
];

const MODELS = [
  { name: "Lean Model", cost: "~$700K", unit: "per year", desc: "Core founding team, minimal overhead, focused on MVP launch and early traction." },
  { name: "Growth Model", cost: "$1.3M-$1.5M", unit: "per year", desc: "Full team scaling across dev, marketing, sales, and operations for rapid expansion.", dark: true },
];

const VALUES = [
  { icon: Compass, title: "Localized Discovery", desc: "We build for the density of one city at a time, owning curation before scale." },
  { icon: Users, title: "Community Engagement", desc: "Local promoters, crew partners, and social-first product decisions drive everything." },
  { icon: Ticket, title: "Dynamic Pricing", desc: "Fair, demand-aware pricing that fills rooms and rewards early fans." },
];

const Careers = () => (
  <div data-testid="page-careers" className="pt-28 sm:pt-32">
    <section className="bg-white">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <Eyebrow>Careers</Eyebrow>
          <h1 className="mt-4 font-display text-3xl font-black leading-[1.08] tracking-tight text-charcoal-900 sm:text-4xl lg:text-5xl">
            Build the team that owns live event discovery.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-steel sm:text-lg">
            We're assembling a lean, high-agency team across engineering, growth, community, and
            operations, hiring in phases through our first twelve months.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="treated-hover overflow-hidden">
            <img src={OFFICE_IMG} alt="Team working session" className="treated aspect-[4/3] w-full object-cover" loading="lazy" />
          </div>
        </Reveal>
      </div>
    </section>

    <section data-testid="hiring-timeline" className="bg-surface py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow="Hiring Timeline" title="Who we hire, and when." lead="A phased hiring plan that grows the team as the platform proves demand." />
        <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {HIRING.map((h, i) => (
            <motion.div
              key={h.phase}
              data-testid={`hiring-phase-${i}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.6, delay: i * 0.07, ease }}
              className="flex h-full flex-col border border-line bg-white p-6 transition-colors duration-300 hover:border-accent/50"
            >
              <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-accent">{h.phase}</div>
              <ul className="mt-5 flex-1 space-y-2.5">
                {h.roles.map((r) => (
                  <li key={r} className="border-b border-line pb-2.5 text-sm font-medium text-charcoal-900 last:border-0 last:pb-0">
                    {r}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="staffing-models" className="bg-white py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow="Staffing Models" title="Two operating models, one disciplined plan." />
        <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-2">
          {MODELS.map((m, i) => (
            <Reveal key={m.name} delay={i * 0.08}>
              <div
                data-testid={`staffing-model-${m.name.toLowerCase()}`}
                className={`flex h-full flex-col justify-between p-9 ${m.dark ? "bg-charcoal-900 text-white" : "border border-line bg-white"}`}
              >
                <div>
                  <h3 className={`font-display text-xl font-bold ${m.dark ? "text-white" : "text-charcoal-900"}`}>{m.name}</h3>
                  <div className={`mt-6 font-mono text-3xl font-semibold tracking-tight sm:text-4xl ${m.dark ? "text-violet-400" : "text-accent"}`}>
                    {m.cost}
                  </div>
                  <div className={`mt-2 font-mono text-[10px] uppercase tracking-[0.2em] ${m.dark ? "text-slate-500" : "text-steel"}`}>{m.unit}</div>
                  <p className={`mt-6 text-sm leading-relaxed ${m.dark ? "text-slate-400" : "text-steel"}`}>{m.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="culture-values" className="bg-surface py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow="How We Work" title="Three principles from the business plan." />
        <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-3">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div data-testid={`value-card-${i}`} className="h-full border border-line bg-white p-8">
                <span className="inline-flex h-10 w-10 items-center justify-center border border-line bg-surface text-accent">
                  <v.icon className="h-5 w-5" strokeWidth={1.8} />
                </span>
                <h3 className="mt-6 font-display text-lg font-bold text-charcoal-900">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-steel">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-16 border border-line bg-white p-8 sm:p-10" delay={0.1}>
          <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
            <div>
              <h3 className="font-display text-xl font-bold text-charcoal-900">Don't see your role yet?</h3>
              <p className="mt-2 max-w-lg text-sm leading-relaxed text-steel">
                Pick "General Interest" in the application form below. We review every application
                and keep strong candidates in mind for the next hiring phase.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section data-testid="application-section" className="bg-white py-20 sm:py-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <Eyebrow>Apply</Eyebrow>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-charcoal-900 sm:text-3xl lg:text-4xl">
            Apply to True Life Entertainment.
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-steel">
            Choose the role you're after from the hiring plan, add your details, and send it over.
            Every application is reviewed by the founding team as the platform moves through its
            first twelve months.
          </p>
          <ul className="mt-8 space-y-3">
            {["Roles across engineering, growth, community, sales, and operations",
              "Applications reviewed as each hiring phase begins",
              "Strong candidates kept in mind for future phases"].map((point) => (
              <li key={point} className="flex items-start gap-3 border border-line bg-surface p-4 text-sm text-charcoal-900">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                {point}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.1}>
          <ApplicationForm />
        </Reveal>
      </div>
    </section>

    <CTABand testid="careers-cta" />
  </div>
);

export default Careers;
